import { useState, useEffect, useRef, useCallback } from 'react';
import { differenceInDays } from 'date-fns';
import { getConfig } from '../config.js';

/**
 * useMarketData
 * -------------
 * Single source of truth for live market data across the dashboard.
 *
 * Returns one consolidated state object, refreshed on a tunable interval:
 *   - btc:  { spot, history[], status, error, fetchedAt }
 *   - gold: { spot, history[], status, error, fetchedAt }
 *   - fred: { cpi[], ffr[], cpiYoY[], status, error, fetchedAt }
 *   - derived metrics (real yield, latest spread, leadLag correlation, etc.)
 *
 * Architectural notes:
 *  - BTC + Gold spot poll every 60s.
 *  - FRED data is fetched once per session and cached for 6h (it only
 *    updates monthly). A second mount won't re-hit FRED if the cache is fresh.
 *  - Every endpoint has a primary + fallback URL.
 *  - All fetches use AbortController so unmounting cancels in-flight requests.
 *  - State only mutates on success; failures keep the last good values and
 *    flip status to 'stale' so the UI can render a warning banner without
 *    blanking out.
 *  - NO mock data — until a fetch succeeds, status is 'loading' and the
 *    consumer must show a skeleton.
 *
 * Required env vars (read once at module load):
 *   VITE_FRED_API_KEY        — your free FRED key
 *   VITE_ALPHA_VANTAGE_KEY   — Alpha Vantage key (for gold history)
 *
 * If running through a CORS proxy server (which you should — FRED has no CORS),
 * pass `proxyBase: '/api'` and the hook will route through it.
 *
 * @param {Object}  opts
 * @param {string}  [opts.fredKey]        Defaults to import.meta.env.VITE_FRED_API_KEY
 * @param {string}  [opts.alphaKey]       Defaults to import.meta.env.VITE_ALPHA_VANTAGE_KEY
 * @param {string}  [opts.proxyBase=''] Base URL of optional CORS proxy
 * @param {number}  [opts.priceIntervalMs=60_000]  Spot-price refresh cadence
 * @param {number}  [opts.fredCacheMs=6*60*60*1000] Cache TTL for FRED data
 */
export default function useMarketData({
  // The Lambda already has FRED_API_KEY in env; users only need to pass one
  // here if they're running outside Amplify (e.g. local dev without sandbox).
  fredKey = readEnv('VITE_FRED_API_KEY') || null,
  alphaKey = readEnv('VITE_ALPHA_VANTAGE_KEY') || null,
  priceIntervalMs = 60_000,
  fredCacheMs = 6 * 60 * 60 * 1000,
} = {}) {
  // ── Granular state per source ─────────────────────────────────────────────
  const [btc, setBtc]     = useState({ spot: null, history: [], status: 'loading', error: null, fetchedAt: null });
  const [gold, setGold]   = useState({ spot: null, history: [], status: 'loading', error: null, fetchedAt: null });
  const [fred, setFred]   = useState({ cpi: [], ffr: [], ppi: [], dgs10: [], t10y2y: [], m2: [], mmf: [], rrp: [], fedBal: [], cpiYoY: [], ppiYoY: [], status: 'loading', error: null, fetchedAt: null });
  // On-chain — currently just the wallet-count metric, but structured to
  // support more on-chain metrics later without new state shapes. Sourced
  // via the dune-proxy Lambda (any Dune Analytics community query that
  // returns a daily series of BTC address counts will plug in).
  // walletsMin1MSats: filtered series from Dune (≥0.01 BTC daily inflows)
  // totalActiveAddresses: broader baseline from Blockchain.com (any tx,
  //   any size). The two metrics measure different populations on purpose,
  //   so the panel displays them side-by-side to disambiguate.
  const [chain, setChain] = useState({
    walletsMin1MSats: [],
    totalActiveAddresses: [],
    status: 'loading',
    error: null,
    fetchedAt: null,
  });
  // Equity valuation snapshot — currently just S&P 500 trailing P/E from
  // multpl.com, fetched server-side by market-proxy. Refreshes once per
  // market close so the Lambda caches for 1h.
  const [equity, setEquity] = useState({ sp500Pe: null, sp500PeChange: null, status: 'loading', error: null, fetchedAt: null });
  // S&P 500, Nasdaq, and DXY monthly history for M2 correlation overlay.
  const [equityHistory, setEquityHistory] = useState({ sp500: [], nasdaq: [], dxy: [], status: 'loading', error: null, fetchedAt: null });
  // Weekly OHLC for the cycle-signal widget (Heikin Ashi RSI). Refreshed
  // hourly; Yahoo's weekly bar only finalises once per Sunday 00:00 UTC.
  const [btcWeekly, setBtcWeekly] = useState({ bars: [], status: 'loading', error: null, fetchedAt: null });

  // Refs avoid re-creating fetchers on every render (prevents interval drift)
  const abortRefs = useRef({ btc: null, gold: null, fred: null, chain: null, equity: null, equityHistory: null, btcWeekly: null });

  // ─── BTC: CoinGecko primary, Coinbase fallback ─────────────────────────────
  const fetchBTC = useCallback(async () => {
    abortRefs.current.btc?.abort();
    const ctl = new AbortController();
    abortRefs.current.btc = ctl;

    setBtc(prev => ({ ...prev, status: prev.spot ? 'refreshing' : 'loading', error: null }));

    try {
      // Spot price
      const spot = await fetchSpotBTC(ctl.signal);
      // Historical (Yahoo Finance via proxy — most reliable free monthly source)
      const history = await fetchBTCHistory(null, ctl.signal);
      setBtc({ spot, history, status: 'live', error: null, fetchedAt: new Date() });
    } catch (e) {
      if (e.name === 'AbortError') return;
      setBtc(prev => ({
        ...prev,                                  // keep last successful values
        status: prev.spot ? 'stale' : 'error',
        error: e.message,
      }));
    }
  }, []);

  // ─── Gold: GoldPrice.org primary, Alpha Vantage fallback ───────────────────
  const fetchGold = useCallback(async () => {
    abortRefs.current.gold?.abort();
    const ctl = new AbortController();
    abortRefs.current.gold = ctl;

    setGold(prev => ({ ...prev, status: prev.spot ? 'refreshing' : 'loading', error: null }));

    try {
      const spot    = await fetchSpotGold(null, ctl.signal);
      const history = await fetchGoldHistory(null, alphaKey, ctl.signal);
      setGold({ spot, history, status: 'live', error: null, fetchedAt: new Date() });
    } catch (e) {
      if (e.name === 'AbortError') return;
      setGold(prev => ({
        ...prev,
        status: prev.spot ? 'stale' : 'error',
        error: e.message,
      }));
    }
  }, [alphaKey]);

  // ─── FRED: cached fetch for CPI + Fed Funds Rate ──────────────────────────
  const fetchFRED = useCallback(async (force = false) => {
    // No client-side key needed — the Lambda has FRED_API_KEY in env.
    // We only forward fredKey if the caller passed one explicitly (dev mode).

    // Cache check — FRED data updates monthly, no point hammering it
    const cached = readFREDCache();
    if (!force && cached && (Date.now() - cached.fetchedAt < fredCacheMs)) {
      // The cache stores fetchedAt as a number (epoch ms) but state expects a
      // Date object so latestDate() can call .getTime() on it. Re-hydrate.
      setFred({ ...cached, status: 'live', fetchedAt: new Date(cached.fetchedAt) });
      return;
    }

    abortRefs.current.fred?.abort();
    const ctl = new AbortController();
    abortRefs.current.fred = ctl;

    setFred(prev => ({ ...prev, status: prev.cpi.length ? 'refreshing' : 'loading', error: null }));

    try {
      // PPIFIS = PPI Final Demand (BLS's headline producer-price index, the
      // modern successor to PPI All Commodities). Series only goes back to
      // 2009 — older points just won't have a YoY value.
      // DGS10  = 10-Year Treasury constant maturity (daily series).
      // T10Y2Y = 10Y minus 2Y spread (recession-curve indicator).
      // Bond yields refresh daily; the 6h cache TTL keeps invocations cheap.
      // Ask the proxy to downsample DGS10 server-side: monthly closes back
      // to 1962, daily granularity only for the trailing 24 months. The
      // recent-trend sparkline plots 12 months of dailies and the historical
      // overlay aggregates monthly anyway, so this drops payload from
      // ~1.6 MB to ~70 KB without affecting any rendered chart.
      const dgs10DailyAfter = (() => {
        const d = new Date(); d.setMonth(d.getMonth() - 24);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      })();

      // Core series — must succeed for the dashboard to function
      const [cpiObs, ffrObs, ppiObs, dgs10Obs, t10y2yObs, m2Obs] = await Promise.all([
        fetchFREDSeries('CPIAUCSL', fredKey, '2008-01-01', null, ctl.signal),
        fetchFREDSeries('FEDFUNDS', fredKey, '2009-01-01', null, ctl.signal),
        fetchFREDSeries('PPIFIS',   fredKey, '2009-01-01', null, ctl.signal),
        fetchFREDSeries(
          'DGS10', fredKey, '1962-01-01', null, ctl.signal,
          { downsample: 'monthly', dailyAfter: dgs10DailyAfter },
        ),
        fetchFREDSeries('T10Y2Y',   fredKey, '1976-06-01', null, ctl.signal),
        fetchFREDSeries('M2SL',     fredKey, '2000-01-01', null, ctl.signal),
      ]);

      // Supplementary series — nice-to-have, don't break the page on failure
      const [mmfRes, rrpRes, fedBalRes] = await Promise.allSettled([
        fetchFREDSeries('WRMFNS',   fredKey, '2020-01-01', null, ctl.signal),
        fetchFREDSeries('RRPONTSYD', fredKey, '2020-01-01', null, ctl.signal,
          { downsample: 'monthly', dailyAfter: dgs10DailyAfter }),
        fetchFREDSeries('WALCL',    fredKey, '2020-01-01', null, ctl.signal),
      ]);
      const mmfObs = mmfRes.status === 'fulfilled' ? mmfRes.value : [];
      const rrpObs = rrpRes.status === 'fulfilled' ? rrpRes.value : [];
      const fedBalObs = fedBalRes.status === 'fulfilled' ? fedBalRes.value : [];

      const cpiYoY = computeMonthlyYoY(cpiObs);
      const ppiYoY = computeMonthlyYoY(ppiObs);

      const result = {
        cpi: cpiObs, ffr: ffrObs, ppi: ppiObs,
        dgs10: dgs10Obs, t10y2y: t10y2yObs, m2: m2Obs,
        mmf: mmfObs, rrp: rrpObs, fedBal: fedBalObs,
        cpiYoY, ppiYoY,
        fetchedAt: Date.now(),
      };
      writeFREDCache(result);
      setFred({ ...result, status: 'live', error: null, fetchedAt: new Date(result.fetchedAt) });
    } catch (e) {
      if (e.name === 'AbortError') return;
      setFred(prev => ({
        ...prev,
        status: prev.cpi.length ? 'stale' : 'error',
        error: e.message,
      }));
    }
  }, [fredKey, fredCacheMs]);

  // ─── On-chain: wallet count ≥ 1M sats (cached daily) ──────────────────────
  // The underlying Dune Analytics query refreshes at most daily, so the Lambda
  // already caches for 1h. We add a session-scoped client cache so navigation
  // within the SPA doesn't re-fetch on every mount.
  const fetchChain = useCallback(async (force = false) => {
    const cached = readChainCache();
    if (!force && cached && (Date.now() - cached.fetchedAt < fredCacheMs)) {
      setChain({ ...cached, status: 'live', fetchedAt: new Date(cached.fetchedAt) });
      return;
    }

    abortRefs.current.chain?.abort();
    const ctl = new AbortController();
    abortRefs.current.chain = ctl;

    setChain(prev => ({
      ...prev,
      status: prev.walletsMin1MSats?.length ? 'refreshing' : 'loading',
      error: null,
    }));

    // Two independent fetches in parallel. Use allSettled so a failure in
    // one source (e.g. Dune schedule expired) doesn't hide the other —
    // we want users to see whichever series is still healthy.
    const [walletsRes, activeRes] = await Promise.allSettled([
      fetchOnchainMetric('wallets-min-1m-sats', ctl.signal),
      fetchActiveAddressesBaseline(ctl.signal),
    ]);

    if (ctl.signal.aborted) return;

    const walletsMin1MSats   = walletsRes.status === 'fulfilled' ? walletsRes.value : [];
    const totalActiveAddresses = activeRes.status === 'fulfilled' ? activeRes.value : [];
    const errors = [walletsRes, activeRes]
      .filter(r => r.status === 'rejected')
      .map(r => r.reason?.message ?? String(r.reason));

    if (!walletsMin1MSats.length && !totalActiveAddresses.length) {
      setChain(prev => ({
        ...prev,
        status: prev.walletsMin1MSats?.length ? 'stale' : 'error',
        error: errors.join(' · ') || 'Both on-chain sources unavailable',
      }));
      return;
    }

    const result = { walletsMin1MSats, totalActiveAddresses, fetchedAt: Date.now() };
    writeChainCache(result);
    setChain({
      ...result,
      // Promote to 'live' when both succeed; flag 'stale' when one fails
      // but the other delivered something usable.
      status: errors.length ? 'stale' : 'live',
      error: errors.length ? errors.join(' · ') : null,
      fetchedAt: new Date(result.fetchedAt),
    });
  }, [fredCacheMs]);

  // ─── Equity valuation: S&P 500 trailing P/E (cached hourly) ───────────────
  // multpl publishes once per market close; the Lambda caches for 1h so
  // mounting many components or timer ticks doesn't burn cycles.
  const fetchEquity = useCallback(async (force = false) => {
    const cached = readEquityCache();
    if (!force && cached && (Date.now() - cached.fetchedAt < fredCacheMs)) {
      setEquity({ ...cached, status: 'live', fetchedAt: new Date(cached.fetchedAt) });
      return;
    }

    abortRefs.current.equity?.abort();
    const ctl = new AbortController();
    abortRefs.current.equity = ctl;

    setEquity(prev => ({
      ...prev,
      status: prev.sp500Pe != null ? 'refreshing' : 'loading',
      error: null,
    }));

    try {
      const { marketProxyUrl } = getConfig();
      const url = appendQuery(marketProxyUrl, { source: 'sp500-pe' });
      const r = await fetch(url, { signal: ctl.signal });
      if (!r.ok) throw new Error(`S&P P/E HTTP ${r.status}`);
      const data = await r.json();
      if (data?.error_message) throw new Error(data.error_message);
      const result = {
        sp500Pe: data.value,
        sp500PeChange: data.change ?? null,
        fetchedAt: Date.now(),
      };
      writeEquityCache(result);
      setEquity({ ...result, status: 'live', error: null, fetchedAt: new Date(result.fetchedAt) });
    } catch (e) {
      if (e.name === 'AbortError') return;
      setEquity(prev => ({
        ...prev,
        status: prev.sp500Pe != null ? 'stale' : 'error',
        error: e.message,
      }));
    }
  }, [fredCacheMs]);

  // ─── Equity history: S&P 500 + Nasdaq monthly (cached hourly) ─────────────
  const fetchEquityHistory = useCallback(async (force = false) => {
    const cached = readEquityHistoryCache();
    if (!force && cached && (Date.now() - cached.fetchedAt < fredCacheMs)) {
      setEquityHistory({ ...cached, status: 'live', fetchedAt: new Date(cached.fetchedAt) });
      return;
    }

    abortRefs.current.equityHistory?.abort();
    const ctl = new AbortController();
    abortRefs.current.equityHistory = ctl;

    setEquityHistory(prev => ({
      ...prev,
      status: prev.sp500?.length ? 'refreshing' : 'loading',
      error: null,
    }));

    try {
      const { marketProxyUrl } = getConfig();
      const [sp500Res, nasdaqRes, dxyRes] = await Promise.allSettled([
        fetch(appendQuery(marketProxyUrl, { source: 'sp500-history' }), { signal: ctl.signal }),
        fetch(appendQuery(marketProxyUrl, { source: 'nasdaq-history' }), { signal: ctl.signal }),
        fetch(appendQuery(marketProxyUrl, { source: 'dxy-history' }), { signal: ctl.signal }),
      ]);

      const parseSeries = (arr) => (Array.isArray(arr) ? arr : [])
        .map(p => ({ date: parseDate(p.date), value: p.value }))
        .filter(p => p.date && Number.isFinite(p.value))
        .sort((a, b) => a.date - b.date);

      const parseRes = async (res) => {
        if (res.status === 'rejected') return [];
        const r = res.value;
        if (!r.ok) return [];
        const data = await r.json();
        if (data?.error_message) return [];
        return parseSeries(data);
      };

      const sp500 = await parseRes(sp500Res);
      const nasdaq = await parseRes(nasdaqRes);
      const dxy = await parseRes(dxyRes);

      if (!sp500.length && !nasdaq.length) throw new Error('Both equity sources failed');

      const result = { sp500, nasdaq, dxy, fetchedAt: Date.now() };
      writeEquityHistoryCache(result);
      setEquityHistory({ ...result, status: 'live', error: null, fetchedAt: new Date(result.fetchedAt) });
    } catch (e) {
      if (e.name === 'AbortError') return;
      setEquityHistory(prev => ({
        ...prev,
        status: prev.sp500?.length ? 'stale' : 'error',
        error: e.message,
      }));
    }
  }, [fredCacheMs]);

  // ─── Weekly BTC OHLC for the cycle-signal widget (Heikin Ashi + RSI) ──────
  // Refreshed once per polling tick but gated by the same fredCacheMs TTL
  // because weekly bars only update once a week — anything more frequent
  // is wasted bandwidth.
  const fetchBtcWeekly = useCallback(async (force = false) => {
    const cached = readBtcWeeklyCache();
    if (!force && cached && (Date.now() - cached.fetchedAt < fredCacheMs)) {
      setBtcWeekly({ ...cached, status: 'live', fetchedAt: new Date(cached.fetchedAt) });
      return;
    }

    abortRefs.current.btcWeekly?.abort();
    const ctl = new AbortController();
    abortRefs.current.btcWeekly = ctl;

    setBtcWeekly(prev => ({
      ...prev,
      status: prev.bars?.length ? 'refreshing' : 'loading',
      error: null,
    }));

    try {
      const { marketProxyUrl } = getConfig();
      const url = appendQuery(marketProxyUrl, { source: 'btc-weekly-ohlc' });
      const r = await fetch(url, { signal: ctl.signal });
      if (!r.ok) throw new Error(`BTC weekly OHLC HTTP ${r.status}`);
      const data = await r.json();
      if (data?.error_message) throw new Error(data.error_message);
      // Hydrate string dates → Date objects so consumers can do date math
      // directly (matches the shape we use for FRED / chain / equity).
      const bars = (data.bars || []).map(b => {
        if (!b?.date) return null;
        const [y, m, d] = String(b.date).split('-').map(Number);
        if (!y || !m) return null;
        return {
          date: new Date(y, m - 1, d || 1),
          open:  b.open,
          high:  b.high,
          low:   b.low,
          close: b.close,
        };
      }).filter(Boolean);
      const result = { bars, fetchedAt: Date.now() };
      writeBtcWeeklyCache(result);
      setBtcWeekly({ ...result, status: 'live', error: null, fetchedAt: new Date(result.fetchedAt) });
    } catch (e) {
      if (e.name === 'AbortError') return;
      setBtcWeekly(prev => ({
        ...prev,
        status: prev.bars?.length ? 'stale' : 'error',
        error: e.message,
      }));
    }
  }, [fredCacheMs]);

  // ─── Lifecycle: initial fetch + polling ────────────────────────────────────
  useEffect(() => {
    fetchBTC();
    fetchGold();
    fetchFRED();
    fetchChain();
    fetchEquity();
    fetchEquityHistory();
    fetchBtcWeekly();

    const id = setInterval(() => {
      fetchBTC();
      fetchGold();
      // FRED / on-chain / equity / weekly-OHLC are gated on their own caches
      // so calling on every tick is safe — they'll no-op until cache expires.
      fetchFRED();
      fetchChain();
      fetchEquity();
      fetchEquityHistory();
      fetchBtcWeekly();
    }, priceIntervalMs);

    return () => {
      clearInterval(id);
      Object.values(abortRefs.current).forEach(ctl => ctl?.abort());
    };
  }, [fetchBTC, fetchGold, fetchFRED, fetchChain, fetchEquity, fetchEquityHistory, fetchBtcWeekly, priceIntervalMs]);

  // ─── Derived metrics: pre-compute everything the UI needs ─────────────────
  const derived = computeDerivedMetrics({ btc, gold, fred });

  return {
    btc,
    gold,
    fred,
    chain,
    equity,
    equityHistory,
    btcWeekly,
    derived,
    isLoading: btc.status === 'loading' || gold.status === 'loading' || fred.status === 'loading',
    isLive:    btc.status === 'live' && gold.status === 'live' && fred.status === 'live',
    refetch:   {
      btc:       fetchBTC,
      gold:      fetchGold,
      fred:      () => fetchFRED(true),
      chain:     () => fetchChain(true),
      equity:    () => fetchEquity(true),
      equityHistory: () => fetchEquityHistory(true),
      btcWeekly: () => fetchBtcWeekly(true),
    },
    lastUpdated: latestDate([btc.fetchedAt, gold.fetchedAt, fred.fetchedAt, chain.fetchedAt, equity.fetchedAt, equityHistory.fetchedAt, btcWeekly.fetchedAt]),
  };
}

// ============================================================================
// Fetcher implementations — broken out for testability
// ============================================================================

async function fetchSpotBTC(signal) {
  // Route through market-proxy so we don't hit CoinGecko's free-tier
  // rate limit (~10-30 rpm per IP) from the browser. The Lambda has its
  // own 30s in-memory cache so the browser refresh cadence (60s) burns
  // at most 2 upstream calls per minute *across all users*, not per user.
  const { marketProxyUrl } = getConfig();
  const r = await fetch(appendQuery(marketProxyUrl, { source: 'btc-spot' }), { signal });
  if (!r.ok) throw new Error(`BTC spot HTTP ${r.status}`);
  const d = await r.json();
  if (d?.error_message) throw new Error(d.error_message);
  const p = d?.price;
  if (!Number.isFinite(p)) throw new Error('BTC spot returned invalid price');
  return p;
}

async function fetchBTCHistory(_proxyBase, signal) {
  // Yahoo Finance — long monthly history with no auth, served via the
  // market-proxy Lambda (?source=btc-history) because Yahoo doesn't set CORS.
  const { marketProxyUrl } = getConfig();
  const url = appendQuery(marketProxyUrl, { source: 'btc-history' });
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`BTC history HTTP ${r.status}`);
  const data = await r.json();
  const result = data?.chart?.result?.[0];
  if (!result?.timestamp) throw new Error('Invalid BTC history response');
  const ts = result.timestamp;
  const closes = result.indicators?.quote?.[0]?.close || [];
  return ts.map((t, i) => ({
    date: new Date(t * 1000),
    value: closes[i],
  })).filter(p => Number.isFinite(p.value));
}

async function fetchSpotGold(_proxyBase, signal) {
  // Always go through the market-proxy Lambda — GoldPrice.org's CORS is
  // unreliable across regions, and the Lambda also normalises the response shape.
  const { marketProxyUrl } = getConfig();
  const r = await fetch(appendQuery(marketProxyUrl, { source: 'gold-spot' }), { signal });
  if (!r.ok) throw new Error(`Gold spot HTTP ${r.status}`);
  const d = await r.json();
  if (d?.error_message) throw new Error(d.error_message);
  const p = d?.price;
  if (!Number.isFinite(p)) throw new Error('Gold spot returned invalid price');
  return p;
}

async function fetchGoldHistory(_proxyBase, _alphaKey, signal) {
  // Yahoo Finance GC=F monthly history via market-proxy Lambda (no auth).
  // Stooq was the original source but started returning HTTP 200 with empty
  // bodies in mid-2026, which broke the Gold → Bitcoin Lead Signal panel.
  const { marketProxyUrl } = getConfig();
  const r = await fetch(appendQuery(marketProxyUrl, { source: 'gold-history' }), { signal });
  if (!r.ok) throw new Error(`Gold history HTTP ${r.status}`);
  const arr = await r.json();
  if (arr?.error_message) throw new Error(arr.error_message);
  return (Array.isArray(arr) ? arr : [])
    .map(p => ({ date: parseDate(p.date), value: p.value }))
    .filter(p => p.date && Number.isFinite(p.value))
    .sort((a, b) => a.date - b.date);
}

// Parse "YYYY-MM-DD" as local-time first-of-month to avoid UTC drift
function parseDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, d || 1);
}

// Append query params, preserving any already on the base URL
function appendQuery(baseUrl, params) {
  const u = new URL(baseUrl, 'http://placeholder.local');
  for (const [k, v] of Object.entries(params)) u.searchParams.set(k, v);
  // Strip the fake origin if it was added (relative URL case)
  return baseUrl.startsWith('http') ? u.toString() : u.pathname + u.search;
}

async function fetchFREDSeries(seriesId, key, observationStart, _proxyBase, signal, opts = {}) {
  const { fredProxyUrl } = getConfig();
  const params = {
    series_id: seriesId,
    file_type: 'json',
    sort_order: 'asc',
    observation_start: observationStart,
  };
  // The Lambda has its own FRED_API_KEY secret; only forward a user-supplied
  // key if they passed one (e.g. for local dev without secrets).
  if (key) params.api_key = key;

  // Optional server-side downsample — for high-cardinality daily series
  // (DGS10 since 1962 = ~16k obs ≈ 1.6 MB) we ask the Lambda to keep one
  // obs per month for the historical window and full daily granularity
  // only on/after `dailyAfter`. Cuts payload by 90%+ with no visible loss
  // of fidelity in the regions actually plotted at high resolution.
  if (opts.downsample === 'monthly') {
    params.downsample = 'monthly';
    if (opts.dailyAfter) params.downsample_after = opts.dailyAfter;
  }

  const url = appendQuery(fredProxyUrl, params);
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`${seriesId} HTTP ${r.status}`);
  const data = await r.json();
  if (data.error_code || data.error_message) {
    throw new Error(data.error_message || `FRED error ${data.error_code}`);
  }
  return (data.observations || [])
    .filter(o => o.value !== '.')
    .map(o => {
      // Parse "YYYY-MM-DD" as local-time first-of-month to avoid UTC drift
      const [y, m, d] = o.date.split('-').map(Number);
      return { date: new Date(y, m - 1, d), value: parseFloat(o.value) };
    });
}

/**
 * Fetch an on-chain metric via the dune-proxy Lambda.
 *
 * The proxy:
 *   1. Looks up a SSM-stored Dune query ID for the requested metric
 *   2. Calls GET https://api.dune.com/api/v1/query/{id}/results
 *   3. Auto-detects the date / value columns in the result rows
 *   4. Normalises to `[{date: "YYYY-MM-DD", value: number}]` ascending
 *
 * `metric` is one of the keys in METRIC_TO_QUERY_ENV in the Lambda handler:
 *   - 'wallets-min-1m-sats'  → BTC addresses with ≥ 0.01 BTC
 *
 * Configuring the underlying query is done by setting these SSM secrets and
 * redeploying the sandbox (Rule #3 — use printf, not echo):
 *   DUNE_API_KEY            = the Dune API key
 *   DUNE_WALLETS_QUERY_ID   = numeric ID of the Dune community query
 */
async function fetchOnchainMetric(metric, signal) {
  const { onchainProxyUrl } = getConfig();
  const url = appendQuery(onchainProxyUrl, { metric });
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`On-chain proxy HTTP ${r.status}`);
  const data = await r.json();
  if (data?.error_message) throw new Error(data.error_message);
  return parsePointsArray(data.points);
}

/**
 * Fetch the broader Blockchain.com daily unique-addresses series via the
 * market-proxy Lambda. This is the "any size, any direction" baseline we
 * show next to the filtered Dune metric so users can see both numbers and
 * understand the difference in methodology rather than misreading either.
 *
 * Returned shape matches fetchOnchainMetric: [{date: Date, value: number}]
 * sorted ascending.
 */
async function fetchActiveAddressesBaseline(signal) {
  const { marketProxyUrl } = getConfig();
  const url = appendQuery(marketProxyUrl, { source: 'btc-active-addresses' });
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`Active-addresses HTTP ${r.status}`);
  const data = await r.json();
  if (data?.error_message) throw new Error(data.error_message);
  return parsePointsArray(data.points);
}

// Shared helper — both Dune and Blockchain.com proxies emit the same
// {date: 'YYYY-MM-DD', value: number} shape, so we parse them identically.
function parsePointsArray(arr) {
  return (arr || [])
    .map(p => {
      if (!p?.date || !Number.isFinite(p.value)) return null;
      const [y, m, d] = String(p.date).split('-').map(Number);
      if (!y || !m) return null;
      return { date: new Date(y, m - 1, d || 1), value: p.value };
    })
    .filter(Boolean)
    .sort((a, b) => a.date - b.date);
}

// ============================================================================
// Derived metrics — pure function, no side effects
// ============================================================================

function computeDerivedMetrics({ btc, gold, fred }) {
  // Latest CPI YoY and FFR — walk back through trailing nulls (FRED's
  // staggered release schedule means one series may publish before another)
  const latestCpiYoY = lastValid(fred.cpiYoY);
  const latestFfr    = lastValid(fred.ffr);

  const cpi = latestCpiYoY?.value ?? null;
  const ffr = latestFfr?.value ?? null;
  const realYield = (cpi != null && ffr != null) ? ffr - cpi : null;

  // Common latest month between CPI and FFR — for the "spread" headline
  const sameMonth = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
  const latestCommonDate = (latestCpiYoY && latestFfr && sameMonth(latestCpiYoY.date, latestFfr.date))
    ? latestCpiYoY.date
    : (latestCpiYoY?.date && latestFfr?.date
        ? (latestCpiYoY.date < latestFfr.date ? latestCpiYoY.date : latestFfr.date)
        : null);

  // Lead-lag correlation: gold's monthly returns vs BTC's monthly returns
  // shifted by N months. Returns an object keyed by lag (0..12).
  const leadLagCorrelation = computeLeadLag(gold.history, btc.history);

  // 6-month gold momentum (the standard lead signal)
  const goldMomentum = computeMomentum(gold.history, 6);
  const goldMomentum3 = computeMomentum(gold.history, 3);

  return {
    cpi, ffr, realYield,
    latestCommonDate,
    leadLagCorrelation,
    bestLag: bestLagFromCorrelation(leadLagCorrelation),
    goldMomentum6m: goldMomentum,
    goldMomentum3m: goldMomentum3,
    // 30-day spread label for the live ribbon ("CPI 4.0% vs FFR 3.64% | Real Yield: -0.36%")
    spreadHeadline: (cpi != null && ffr != null)
      ? `Current CPI (${cpi.toFixed(2)}%) vs FFR (${ffr.toFixed(2)}%) | Real Yield: ${(realYield >= 0 ? '+' : '')}${realYield.toFixed(2)}%`
      : null,
  };
}

function lastValid(arr) {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i] != null && Number.isFinite(arr[i].value)) return arr[i];
  }
  return null;
}

// Compute YoY% for a monthly FRED series by anchoring on the SAME calendar
// month one year prior. Naïve i / i-12 lookups drift by one whenever the
// series has a missing month (e.g. CPIAUCSL was missing Oct 2025 as of
// mid-2026, which produced a false 3.95% reading instead of 3.78% for Apr).
function computeMonthlyYoY(observations) {
  if (!Array.isArray(observations) || !observations.length) return [];
  const byMonthKey = new Map();
  for (const o of observations) {
    if (!o?.date) continue;
    const t = o.date instanceof Date ? o.date : new Date(o.date);
    if (!Number.isFinite(t?.getTime())) continue;
    byMonthKey.set(`${t.getFullYear()}-${t.getMonth()}`, o);
  }
  const out = [];
  for (const o of observations) {
    if (!o?.date || !Number.isFinite(o.value) || o.value <= 0) continue;
    const t = o.date instanceof Date ? o.date : new Date(o.date);
    if (!Number.isFinite(t?.getTime())) continue;
    const prior = byMonthKey.get(`${t.getFullYear() - 1}-${t.getMonth()}`);
    if (!prior || !Number.isFinite(prior.value) || prior.value <= 0) continue;
    out.push({
      date: o.date,
      value: parseFloat(((o.value / prior.value - 1) * 100).toFixed(2)),
    });
  }
  return out;
}

function computeMomentum(series, monthsBack) {
  if (!series || series.length < monthsBack + 1) return null;
  const latest = series[series.length - 1].value;
  const prior  = series[series.length - 1 - monthsBack].value;
  if (!Number.isFinite(latest) || !Number.isFinite(prior) || prior === 0) return null;
  return ((latest - prior) / prior) * 100;
}

// Pearson correlation of monthly log returns at each lag from 0 to 12 months.
// Returns: [{ lag, r, score }] where score maps r ∈ [-0.3, 0.5] → [0, 100].
function computeLeadLag(goldHistory, btcHistory) {
  if (!goldHistory?.length || !btcHistory?.length) return [];
  const N = Math.min(goldHistory.length, btcHistory.length, 60); // last 5 years
  const goldVals = goldHistory.slice(-N).map(p => p.value);
  const btcVals  = btcHistory.slice(-N).map(p => p.value);

  const out = [];
  for (let lag = 0; lag <= 12; lag++) {
    const goldRet = [];
    const btcRet  = [];
    for (let i = 1; i < goldVals.length; i++) {
      const g0 = goldVals[i - 1], g1 = goldVals[i];
      const b0 = btcVals[i - 1 + lag], b1 = btcVals[i + lag];
      if (!g0 || !g1 || !b0 || !b1) continue;
      goldRet.push(Math.log(g1 / g0));
      btcRet.push(Math.log(b1 / b0));
    }
    const r = pearson(goldRet, btcRet);
    const score = Math.round(Math.max(0, Math.min(100, ((r + 0.3) / 0.8) * 100)));
    out.push({ lag, r, score });
  }
  return out;
}

function pearson(a, b) {
  const n = Math.min(a.length, b.length);
  if (n < 5) return 0;
  const mx = a.slice(0, n).reduce((s, v) => s + v, 0) / n;
  const my = b.slice(0, n).reduce((s, v) => s + v, 0) / n;
  let num = 0, dx = 0, dy = 0;
  for (let i = 0; i < n; i++) {
    const ax = a[i] - mx, ay = b[i] - my;
    num += ax * ay; dx += ax * ax; dy += ay * ay;
  }
  return dx && dy ? num / Math.sqrt(dx * dy) : 0;
}

function bestLagFromCorrelation(corrArr) {
  if (!corrArr?.length) return null;
  return corrArr.reduce((best, c) => c.score > (best?.score ?? -Infinity) ? c : best, null);
}

// ============================================================================
// FRED cache — survives re-mounts within a session via in-memory module state
// (intentionally NOT localStorage so private-mode users are unaffected; we
// just want to avoid hammering FRED when the user navigates around the SPA)
// ============================================================================

let _fredCache = null;
function readFREDCache()  { return _fredCache; }
function writeFREDCache(v) { _fredCache = v; }

let _chainCache = null;
function readChainCache()  { return _chainCache; }
function writeChainCache(v) { _chainCache = v; }

let _equityCache = null;
function readEquityCache()  { return _equityCache; }
function writeEquityCache(v) { _equityCache = v; }

let _equityHistoryCache = null;
function readEquityHistoryCache()  { return _equityHistoryCache; }
function writeEquityHistoryCache(v) { _equityHistoryCache = v; }

let _btcWeeklyCache = null;
function readBtcWeeklyCache()  { return _btcWeeklyCache; }
function writeBtcWeeklyCache(v) { _btcWeeklyCache = v; }

// ============================================================================
// Helpers
// ============================================================================

function readEnv(key) {
  // Vite-style env access — won't throw if running outside Vite
  try { return import.meta.env?.[key] ?? null; }
  catch { return null; }
}

function latestDate(dates) {
  // Defensive — accept Date, number (epoch ms), or ISO string.
  // Filters out nullish/invalid entries so a single malformed value doesn't crash.
  const ts = dates
    .map(d => {
      if (d == null) return null;
      if (d instanceof Date) return d.getTime();
      if (typeof d === 'number') return d;
      if (typeof d === 'string') {
        const t = Date.parse(d);
        return Number.isFinite(t) ? t : null;
      }
      return null;
    })
    .filter(t => t != null && Number.isFinite(t));
  if (!ts.length) return null;
  return new Date(Math.max(...ts));
}
